'use client';

import React, { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { whyChooseData } from '@/data/whyChooseUs';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

const THEME = {
  accentGold: '#C89B3C',
  headingText: '#1F1F1F',
  descriptionText: '#777777',
  cardBg: '#FAFAFA',
  borderColor: '#ECECEC',
};

export const WhyChooseSection: React.FC = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useGSAP(
    () => {
      if (!containerRef.current) return;

      const cardElements = cardsRef.current.filter(
        (el): el is HTMLDivElement => el !== null
      );

      gsap.fromTo(
        cardElements,
        { opacity: 0, y: 35 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          stagger: 0.1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top 85%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    },
    { scope: containerRef }
  );

  return (
    <section
      ref={containerRef}
      aria-label="Why Choose Us"
      className="w-full bg-white py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 xl:px-12 flex justify-center overflow-hidden"
    >
      <div className="w-full max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-12 md:mb-16 px-2 sm:px-0">
          <span className="text-xs sm:text-sm font-bold tracking-[0.2em] text-[#C89B3C] uppercase block mb-2 sm:mb-3">
            THE EXCELLENCE STANDARD
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#1F1F1F] tracking-tight uppercase">
            WHY CHOOSE US
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-[#777777] mt-3 sm:mt-4 max-w-2xl mx-auto font-normal leading-relaxed">
            Partnering with global buyers to deliver unparalleled quality, custom manufacturing, and dependable supply chain logistics.
          </p>
        </div>

        {/* Responsive Grid System */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {whyChooseData.map((item, index) => {
            const Icon = item.icon;

            return (
              <div
                key={item.id}
                ref={(el) => {
                  cardsRef.current[index] = el;
                }}
                className="group relative bg-[#FAFAFA] hover:bg-white p-5 sm:p-6 md:p-8 rounded-xl border border-[#ECECEC] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl cursor-pointer flex flex-col justify-between h-full"
              >
                {/* Accent Highlight Bar on Hover */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-[#C89B3C] rounded-t-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                <div>
                  {/* Icon Container */}
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg bg-white border border-[#ECECEC] shadow-sm flex items-center justify-center mb-4 sm:mb-6 group-hover:border-[#C89B3C] transition-colors duration-300 shrink-0">
                    <Icon
                      size={26}
                      color={THEME.accentGold}
                      strokeWidth={1.6}
                      className="transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6 sm:w-7 sm:h-7"
                      aria-hidden="true"
                    />
                  </div>

                  {/* Content */}
                  <h3 className="text-lg sm:text-xl font-bold text-[#1F1F1F] group-hover:text-[#C89B3C] transition-colors duration-300 leading-snug">
                    {item.title}
                  </h3>
                  <p className="text-sm sm:text-base text-[#777777] mt-2 sm:mt-3 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;